/**
 * Cozinhas (estações de produção): o cardápio pode ser preparado em várias
 * estações (bar, carnes, sobremesas). Cada cozinha aparece no monitor da
 * cozinha (KDS) com os SEUS itens; produtos sem direcionamento preparam na
 * cozinha principal.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { kitchensApi, categories as categoriesApi, type Kitchen } from '../lib/api.js'
import { Button, Input, Empty, Spinner, toast } from '../components/ui/index.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

export function Kitchens() {
  const { t, lang } = useI18n()
  const qc = useQueryClient()
  const [newName, setNewName] = useState('')
  const [assignCat, setAssignCat] = useState('')
  const [assignKitchen, setAssignKitchen] = useState('__none__')

  const { data, isLoading } = useQuery({
    queryKey: ['kitchens'],
    queryFn: () => kitchensApi.list(),
  })
  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: categoriesApi.list })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['kitchens'] })
    void qc.invalidateQueries({ queryKey: ['products'] })
  }

  const createMut = useMutation({
    mutationFn: () => kitchensApi.create({ name: newName }),
    onSuccess: () => {
      setNewName('')
      invalidate()
      toast.success(t('admin.kitchens.created'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => kitchensApi.update(id, { name }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message),
  })

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      kitchensApi.update(id, { active }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => kitchensApi.remove(id),
    onSuccess: (_d, id) => {
      invalidate()
      if (assignKitchen === id) setAssignKitchen('__none__')
      toast.success(t('admin.kitchens.deleted'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const assignMut = useMutation({
    mutationFn: () => {
      const categoryId = assignCat || cats?.[0]?.id || ''
      if (!categoryId) throw new Error(t('admin.kitchens.no_categories'))
      return kitchensApi.assignCategory({
        category_id: categoryId,
        kitchen_id: assignKitchen === '__none__' ? null : assignKitchen,
      })
    },
    onSuccess: ({ updated }) => {
      invalidate()
      toast.success(t('admin.kitchens.assigned', { n: String(updated) }))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )

  const kitchens: Kitchen[] = data?.kitchens ?? []

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.kitchens.title')}</h1>
        <p className="text-sm text-gray-500">{t('admin.kitchens.subtitle')}</p>
      </div>

      {/* Cozinha principal (sem direcionamento) — sempre presente */}
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-gray-900">🍽 {t('admin.kitchens.main')}</p>
            <p className="text-xs text-gray-500">{t('admin.kitchens.main_hint')}</p>
          </div>
          <span className="text-sm font-medium text-indigo-700">
            {t('admin.kitchens.products', { n: String(data?.unassigned_count ?? 0) })}
          </span>
        </div>
      </div>

      {/* Criar */}
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (newName.trim()) createMut.mutate()
        }}
      >
        <Input
          placeholder={t('admin.kitchens.name_placeholder')}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={60}
        />
        <Button type="submit" disabled={!newName.trim() || createMut.isPending}>
          {t('admin.kitchens.create')}
        </Button>
      </form>

      {/* Lista */}
      {kitchens.length === 0 ? (
        <Empty title={t('admin.kitchens.empty')} description={t('admin.kitchens.empty_hint')} />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
          {kitchens.map((k) => (
            <KitchenRow
              key={k.id}
              kitchen={k}
              onRename={(name) => renameMut.mutate({ id: k.id, name })}
              onToggle={(active) => toggleMut.mutate({ id: k.id, active })}
              onDelete={() => {
                if (window.confirm(t('admin.kitchens.confirm_delete', { name: k.name })))
                  deleteMut.mutate(k.id)
              }}
            />
          ))}
        </ul>
      )}

      {/* Direcionamento por categoria */}
      {cats && cats.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div>
            <h2 className="font-semibold text-gray-900">{t('admin.kitchens.assign_title')}</h2>
            <p className="text-xs text-gray-500">{t('admin.kitchens.assign_hint')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={assignCat || cats[0]?.id}
              onChange={(e) => setAssignCat(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {cats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.translations.find((x) => x.locale === lang)?.name ??
                    c.translations[0]?.name ??
                    c.id}
                </option>
              ))}
            </select>
            <select
              value={assignKitchen}
              onChange={(e) => setAssignKitchen(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="__none__">{t('admin.kitchens.main')}</option>
              {kitchens.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              disabled={assignMut.isPending}
              onClick={() => assignMut.mutate()}
            >
              {t('admin.kitchens.assign_apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function KitchenRow({
  kitchen,
  onRename,
  onToggle,
  onDelete,
}: {
  kitchen: Kitchen
  onRename: (name: string) => void
  onToggle: (active: boolean) => void
  onDelete: () => void
}) {
  const { t } = useI18n()
  const [name, setName] = useState(kitchen.name)

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => name.trim() && name !== kitchen.name && onRename(name.trim())}
        className={cn(
          'flex-1 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-indigo-400 outline-none text-sm font-medium',
          !kitchen.active && 'line-through text-gray-400',
        )}
        maxLength={60}
      />
      <span className="text-xs text-gray-500 whitespace-nowrap">
        {t('admin.kitchens.products', { n: String(kitchen.product_count) })}
      </span>
      <button
        onClick={() => onToggle(!kitchen.active)}
        className={cn(
          'text-xs px-2 py-1 rounded-md font-medium',
          kitchen.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
        )}
      >
        {kitchen.active ? t('admin.kitchens.active') : t('admin.kitchens.inactive')}
      </button>
      <button
        onClick={onDelete}
        className="text-xs px-2 py-1 rounded-md font-medium text-red-600 hover:bg-red-50"
      >
        {t('admin.kitchens.delete')}
      </button>
    </li>
  )
}
